// ==================================================================================
// Digital Approval System (DAS) - Workflow Templates Service
// ==================================================================================
// Service functions for workflow template operations
// ==================================================================================

import { axiosClient } from '../../client/axiosClient';
import { ApiResponse } from '../../types/common.types';
import {
  WorkflowTemplateDto,
  CreateWorkflowTemplateRequest,
  UpdateWorkflowTemplateRequest,
  WorkflowTemplateQueryParameters,
} from '../../types/das/workflowTemplates.types';
import { workflowTemplateEndpoints } from '../../endpoints/das/workflowTemplates.endpoints';

/**
 * Workflow Templates service
 */
export const workflowTemplatesService = {
  /**
   * Get all workflow templates
   */
  getAll: async (
    params?: WorkflowTemplateQueryParameters
  ): Promise<WorkflowTemplateDto[]> => {
    const response = await axiosClient.get<ApiResponse<WorkflowTemplateDto[]>>(
      workflowTemplateEndpoints.getAll(params)
    );
    return response.data.data;
  },

  /**
   * Get workflow template by ID
   */
  getById: async (id: string): Promise<WorkflowTemplateDto> => {
    const response = await axiosClient.get<ApiResponse<WorkflowTemplateDto>>(
      workflowTemplateEndpoints.getById(id)
    );
    return response.data.data;
  },

  /**
   * Get active workflow templates only
   */
  getActive: async (): Promise<WorkflowTemplateDto[]> => {
    const response = await axiosClient.get<ApiResponse<WorkflowTemplateDto[]>>(
      workflowTemplateEndpoints.getActive()
    );
    return response.data.data;
  },

  /**
   * Get workflow templates by category
   */
  getByCategory: async (category: string): Promise<WorkflowTemplateDto[]> => {
    const response = await axiosClient.get<ApiResponse<WorkflowTemplateDto[]>>(
      workflowTemplateEndpoints.getByCategory(category)
    );
    return response.data.data;
  },

  /**
   * Get workflow templates by request type
   */
  getByRequestType: async (requestTypeCode: string): Promise<WorkflowTemplateDto[]> => {
    const response = await axiosClient.get<ApiResponse<WorkflowTemplateDto[]>>(
      workflowTemplateEndpoints.getByRequestType(requestTypeCode)
    );
    return response.data.data;
  },

  /**
   * Create new workflow template
   */
  create: async (request: CreateWorkflowTemplateRequest): Promise<WorkflowTemplateDto> => {
    const response = await axiosClient.post<ApiResponse<WorkflowTemplateDto>>(
      workflowTemplateEndpoints.create(),
      request
    );
    return response.data.data;
  },

  /**
   * Update workflow template
   */
  update: async (
    id: string,
    request: UpdateWorkflowTemplateRequest
  ): Promise<WorkflowTemplateDto> => {
    const response = await axiosClient.put<ApiResponse<WorkflowTemplateDto>>(
      workflowTemplateEndpoints.update(id),
      request
    );
    return response.data.data;
  },

  /**
   * Delete workflow template
   */
  delete: async (id: string): Promise<boolean> => {
    const response = await axiosClient.delete<ApiResponse<boolean>>(
      workflowTemplateEndpoints.delete(id)
    );
    return response.data.data;
  },
};
